import { localizeProviderDisplayName } from '@/utils/providerDisplayName'
import { isLiveFeatureEnabled } from '@/utils/liveFeature'

const PROVIDER_IMG_BASE = '/assets/images/provider_img'

export function getProviderDisplayName (filename) {
  if (!filename) return ''
  const base = String(filename).replace(/\.[^/.]+$/, '')
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export function getProviderImagePath (filename) {
  if (!filename) return ''
  const name = String(filename).trim()
  if (!name) return ''
  const hasExtension = /\.[^./\\]+$/.test(name)
  const file = hasExtension ? name : `${name}.png`
  return `${PROVIDER_IMG_BASE}/${encodeURIComponent(file)}`
}

export function resolveProviderImageKey (item) {
  if (typeof item === 'string') return item
  if (!item || typeof item !== 'object') return ''
  return String(item.name || item.oracle || item.filename || '').trim()
}

export function resolveProviderFilename (item) {
  if (typeof item === 'string') return item
  if (!item || typeof item !== 'object') return ''
  const key = item.oracle || item.filename || item.name || ''
  return String(key).trim()
}

export function resolveProviderName (item, filename) {
  if (item && typeof item === 'object' && item.name) {
    return String(item.name)
  }
  return getProviderDisplayName(filename)
}

export function resolveProviderDisplayName (item, filename) {
  return localizeProviderDisplayName(resolveProviderName(item, filename))
}

export function normalizeProviderItem (item, index) {
  const filename = resolveProviderFilename(item)
  const imageKey = resolveProviderImageKey(item)
  const name = resolveProviderName(item, filename)
  return {
    idx: item && item.idx != null ? item.idx : index,
    filename,
    name,
    displayName: localizeProviderDisplayName(name),
    image: getProviderImagePath(imageKey),
    category: item && item.category != null ? item.category : null,
    raw: item
  }
}

export function normalizeProviders (list) {
  if (!Array.isArray(list)) return []
  return list.map((item, index) => normalizeProviderItem(item, index))
}

export const GAME_CATEGORY = {
  SLOT: 1,
  LIVE: 2,
  FISHING: 3,
  MINI_GAMES: 4,
  SPORTS: 5
}

/** 스포츠 탭에서 사용할 게임사 표시명 */
export const SPORTS_PROVIDER_NAME = 'Bti'

/** 미니게임 탭에서 사용할 게임사 표시명 */
export const MINI_GAME_PROVIDER_NAME = 'Spribe'

function providerNameCandidates (item) {
  if (!item || typeof item !== 'object') return []
  const raw = item.raw
  return [
    item.name,
    item.filename,
    resolveProviderFilename(item),
    raw && raw.name,
    raw && raw.oracle,
    raw && raw.filename
  ]
}

export function providerNameMatches (item, targetName) {
  const target = String(targetName || '').trim().toLowerCase()
  if (!target) return false
  return providerNameCandidates(item).some(value => {
    const name = String(value || '').trim().toLowerCase()
    if (!name) return false
    return name === target || name.replace(/\.[^./\\]+$/, '') === target
  })
}

export function findProvidersByName (list, name) {
  if (!Array.isArray(list)) return []
  return list.filter(item => providerNameMatches(item, name))
}

/** 스포츠·미니게임 전용 탭 게임사 — 라이브/슬롯 목록에서 제외 */
export function isExclusiveTabProvider (item) {
  return providerNameMatches(item, SPORTS_PROVIDER_NAME) ||
    providerNameMatches(item, MINI_GAME_PROVIDER_NAME)
}

export function excludeExclusiveTabProviders (list) {
  if (!Array.isArray(list)) return []
  return list.filter(item => !isExclusiveTabProvider(item))
}

export function resolveProviderGameSymbol (provider) {
  if (!provider || typeof provider !== 'object') return 'lobby'
  const symbol = provider.oracle ||
    provider.mn1 ||
    provider.mn2 ||
    provider.camball ||
    provider.devaPlay ||
    provider.sd ||
  ''
  const value = String(symbol).trim()
  return value || 'lobby'
}

export function getProviderIdx (item) {
  if (!item || typeof item !== 'object') return null
  const idx = item.idx != null ? item.idx : (item.raw && item.raw.idx)
  const num = Number(idx)
  return Number.isFinite(num) ? num : null
}

export function filterProvidersByCategory (list, category) {
  if (!Array.isArray(list)) return []
  return list.filter(item => {
    if (!item || typeof item !== 'object') return false
    return Number(item.category) === Number(category)
  })
}

export function excludeProvidersByCategory (list, category) {
  if (!Array.isArray(list)) return []
  return list.filter(item => {
    if (!item || typeof item !== 'object') return false
    return Number(item.category) !== Number(category)
  })
}

/** 상단/모바일 게임 카테고리 노출 여부 (게임사 1개 이상일 때만 true) */
export function buildGameNavAvailability ({
  providers,
  fishingUsageEnabled = false,
  liveEnabled = isLiveFeatureEnabled()
} = {}) {
  const list = normalizeProviders(providers)
  const live = excludeExclusiveTabProviders(
    filterProvidersByCategory(list, GAME_CATEGORY.LIVE)
  )
  const slots = filterProvidersByCategory(list, GAME_CATEGORY.SLOT)
  const sports = findProvidersByName(list, SPORTS_PROVIDER_NAME)
  const minigames = findProvidersByName(list, MINI_GAME_PROVIDER_NAME)

  return {
    live: !!liveEnabled && live.length > 0,
    slots: slots.length > 0,
    sports: sports.length > 0,
    minigames: minigames.length > 0,
    fishing: !!fishingUsageEnabled
  }
}

export function filterNavByCategoryAvailability (items, availability) {
  if (!Array.isArray(items)) return []
  const map = availability && typeof availability === 'object' ? availability : {}
  return items.filter(item => {
    if (!item || !item.key) return false
    if (item.key === 'home') return true
    if (Object.prototype.hasOwnProperty.call(map, item.key)) {
      return !!map[item.key]
    }
    return true
  })
}

export function filterGameNavItems (items, {
  providers,
  fishingUsageEnabled = false,
  liveEnabled = isLiveFeatureEnabled()
} = {}) {
  const availability = buildGameNavAvailability({ providers, fishingUsageEnabled, liveEnabled })
  return filterNavByCategoryAvailability(items, availability)
}

export function filterVisibleProviders (list, { liveEnabled = isLiveFeatureEnabled() } = {}) {
  let result = excludeExclusiveTabProviders(list)
  if (!liveEnabled) {
    result = excludeProvidersByCategory(result, GAME_CATEGORY.LIVE)
  }
  return result
}
